import React from "react"
import Layout from "../components/layout"
import EmojiPageComponent from "../components/emoji_page"
import { graphql } from "gatsby"
import { getSrc } from "gatsby-plugin-image"
import { SEO } from "../components/seo"

const getTranslations = props => {
  const { translations } = props.data.emoji
  return translations.map(translation => {
    return {
      link: `/${translation.path}/`,
      code: translation.language.code,
      nativeName: translation.language.nativeName,
    }
  })
}

class EmojiPage extends React.PureComponent {
  getBreadcrumbs() {
    const language_code = this.props.data.language.code
    const emoji_path = this.props.data.emojiPath.string
    const { subcategories, categories } = this.props.data.emoji
    const breadcrumbs_list = []
    const breadcrumbs_before = [
      {
        text: this.props.data.homeHeader.string,
        link: language_code === "en" ? `/` : `/${language_code}/`,
      },
      {
        text: this.props.data.emojiHeader.string,
        link:
          language_code === "en"
            ? `/${emoji_path}/`
            : `/${language_code}/${emoji_path}/`,
      },
    ]
    const breadcrumbs_after = [
      {
        text: this.getTitle(),
        link: `/${this.props.data.emoji.path}/`,
      },
    ]
    if (subcategories.length) {
      for (var i = 0; i < subcategories.length; i++) {
        const subcat = subcategories[i]
        const cat = subcat.category
        breadcrumbs_list.push([
          ...breadcrumbs_before,
          {
            text: cat.navName,
            link: `/${cat.path}/`,
          },
          {
            text: subcat.name,
            link: `/${cat.path}/#${subcat.slug}`,
          },
          ...breadcrumbs_after,
        ])
      }
    }
    if (categories.length) {
      for (var j = 0; j < categories.length; j++) {
        const cat = categories[j]
        breadcrumbs_list.push([
          ...breadcrumbs_before,
          {
            text: cat.navName,
            link: `/${cat.path}/`,
          },
          ...breadcrumbs_after,
        ])
      }
    }
    return breadcrumbs_list
  }

  getTitle() {
    const { emoji } = this.props.data
    let { pageTitle, name, parentName } = emoji
    if (!pageTitle) {
      let t = parentName || name
      pageTitle = t.charAt(0).toUpperCase() + t.slice(1)
    }
    return pageTitle
  }

  render() {
    const { pageContext } = this.props
    const { pageSubtitle } = this.props.data.emoji
    return (
      <Layout
        layoutContext={pageContext.layoutContext}
        pageTitle={this.getTitle()}
        pageSubtitle={pageSubtitle}
        breadcrumbs={this.getBreadcrumbs()}
        translatedLinks={getTranslations(this.props)}
        content={<EmojiPageComponent {...this.props} />}
      ></Layout>
    )
  }
}

export default EmojiPage

export const Head = props => {
  const { data } = props
  const { metaTitle, metaDescription } = data.emoji
  const mainImage = data.emoji.mainImage
  return (
    <SEO {...props} translatedLinks={getTranslations(props)}>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      {mainImage && (
        <meta
          property="og:image"
          content={`https://yaytext.com${getSrc(mainImage.image)}`}
        />
      )}
    </SEO>
  )
}

export const pageQuery = graphql`
  query EmojiPageQuery($languageId: String, $emojiId: String) {
    language: yaytextLanguage(languageId: { eq: $languageId }) {
      code
    }
    emoji: yaytextEmoji(
      emojiId: { eq: $emojiId }
      language: { languageId: { eq: $languageId } }
    ) {
      id
      pageTitle
      pageSubtitle
      parentName
      metaTitle
      metaDescription
      description
      name
      path
      codepoints
      isSkinTone
      translations {
        path
        language {
          code
          nativeName
        }
      }
      mainImage {
        image {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, width: 1000, placeholder: NONE)
          }
        }
      }
      subcategories {
        name
        slug
        category {
          navName
          path
        }
      }
      categories {
        navName
        path
      }
      siblings {
        id
        name
        codepoints
        isSkinTone
        tags
        mainImage {
          image {
            childImageSharp {
              gatsbyImageData(layout: CONSTRAINED, width: 1000)
            }
          }
        }
        variants {
          id
          name
          codepoints
          isSkinTone
          tags
          mainImage {
            image {
              childImageSharp {
                gatsbyImageData(layout: CONSTRAINED, width: 1000)
              }
            }
          }
        }
      }
      variants {
        id
        name
        codepoints
        isSkinTone
        mainImage {
          image {
            childImageSharp {
              gatsbyImageData(layout: CONSTRAINED, width: 1000)
            }
          }
        }
      }
      genderVariants {
        id
        name
        codepoints
        isSkinTone
        mainImage {
          image {
            childImageSharp {
              gatsbyImageData(layout: CONSTRAINED, width: 1000)
            }
          }
        }
        variants {
          id
          name
          codepoints
          isSkinTone
          mainImage {
            image {
              childImageSharp {
                gatsbyImageData(layout: CONSTRAINED, width: 1000)
              }
            }
          }
        }
      }
      tags
      emojiVersion {
        version
      }
      unicodeVersion {
        code
        version
        date(formatString: "MMMM, YYYY")
      }
      related {
        id
        name
        parentName
        codepoints
        shortDescription
        path
        siblings {
          name
          codepoints
          variants {
            codepoints
          }
        }
        variants {
          name
          codepoints
          siblings {
            name
            codepoints
          }
        }
        genderVariants {
          name
          codepoints
          variants {
            name
            codepoints
          }
        }
        showGenderVariantsAtTopLevel
      }
    }
    homeHeader: yaytextGlobalString(
      key: { eq: "home-header" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    emojiPath: yaytextGlobalString(
      key: { eq: "emoji-path" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    emojiHeader: yaytextGlobalString(
      key: { eq: "emoji-header" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    copy: yaytextGlobalString(
      key: { eq: "copy" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    copiedExclamation: yaytextGlobalString(
      key: { eq: "copied_exclamation" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
    notifyCopied: yaytextGlobalString(
      key: { eq: "notify-copied" }
      language: { languageId: { eq: $languageId } }
    ) {
      string
    }
  }
`
